import { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SCALE = 2; // template-image rendu à 2x (points PDF -> pixels)

export default function RapportEditor({ rapportId, onClose, onSaved }) {
  const [nom, setNom] = useState('');
  const [systeme, setSysteme] = useState(false);
  const [baseSource, setBaseSource] = useState('');
  const [tableSource, setTableSource] = useState('');
  const [dansMenu, setDansMenu] = useState(false);
  const [dansMenuImpression, setDansMenuImpression] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const [fields, setFields] = useState([]);
  const [bases, setBases] = useState([]);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [colFilter, setColFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [imgUrl] = useState(`${API}/rapports/${rapportId}/template-image?t=${Date.now()}`);

  const imgRef = useRef(null);
  const scrollRef = useRef(null);
  const [natW, setNatW] = useState(0);
  const [drag, setDrag] = useState(null); // {id, startX, startY, fieldX, fieldY}

  // Ajuste le zoom pour que toute la page tienne dans la zone visible
  const fitToPane = (w, h) => {
    const el = scrollRef.current;
    if (!el || !w || !h) return;
    const availW = el.clientWidth - 32;
    const availH = el.clientHeight - 32;
    const z = Math.min(availW / w, availH / h, 1);
    setZoom(Math.max(0.3, Math.round(z * 100) / 100));
  };

  // Chargement initial
  useEffect(() => {
    (async () => {
      const [rRes, bRes] = await Promise.all([
        fetch(`${API}/rapports/${rapportId}`),
        fetch(`${API}/rapports/meta/bases`),
      ]);
      const r = await rRes.json();
      const b = await bRes.json();
      setBases(b || []);
      setNom(r.nom || '');
      setSysteme(!!r.systeme);
      setBaseSource(r.base_source || '');
      setTableSource(r.table_source || '');
      setDansMenu(!!r.dans_menu);
      setDansMenuImpression(!!r.dans_menu_impression);
      setOffsetY((r.config && r.config.offset_y) || 0);
      setFields(((r.config && r.config.fields) || []).map((f, i) => ({
        id: f.id || `${f.column || f.type || 'f'}_${i}`,
        type: f.type || null,
        column: f.column || (f.type ? null : f.id),
        x: f.x || 0, y: f.y || 0, fs: f.fs || 9,
        w: f.w != null ? f.w : (f.type === 'logo' ? 120 : 0),
        h: f.h || 60,
        align: f.align || 'left',
        fit: f.fit !== false,
      })));
    })();
  }, [rapportId]);

  // Charger tables quand la base change
  useEffect(() => {
    if (!baseSource) { setTables([]); return; }
    fetch(`${API}/rapports/meta/tables?base=${encodeURIComponent(baseSource)}`)
      .then(r => r.json()).then(t => setTables(Array.isArray(t) ? t : []))
      .catch(() => setTables([]));
  }, [baseSource]);

  // Charger colonnes quand la table change
  useEffect(() => {
    if (!baseSource || !tableSource) { setColumns([]); return; }
    fetch(`${API}/rapports/meta/columns?base=${encodeURIComponent(baseSource)}&table=${encodeURIComponent(tableSource)}`)
      .then(r => r.json()).then(c => setColumns(Array.isArray(c) ? c : []))
      .catch(() => setColumns([]));
  }, [baseSource, tableSource]);

  const pointFromEvent = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    return {
      x: Math.round((px / (SCALE * zoom)) * 10) / 10,
      y: Math.round((py / (SCALE * zoom)) * 10) / 10,
    };
  };

  // Glisser une colonne ou un élément spécial depuis la palette -> déposer
  const handleDrop = (e) => {
    e.preventDefault();
    const pt = pointFromEvent(e);
    const special = e.dataTransfer.getData('text/special');
    if (special === 'logo') {
      const id = `logo_${Date.now()}`;
      setFields(prev => [...prev, { id, type: 'logo', x: pt.x, y: pt.y, w: 120, h: 60 }]);
      setSelectedId(id);
      return;
    }
    if (special === 'date') {
      const id = `date_${Date.now()}`;
      setFields(prev => [...prev, { id, type: 'date', x: pt.x, y: pt.y, fs: 10 }]);
      setSelectedId(id);
      return;
    }
    if (special === 'region') {
      const id = `region_${Date.now()}`;
      setFields(prev => [...prev, { id, type: 'region', x: pt.x, y: pt.y, fs: 12 }]);
      setSelectedId(id);
      return;
    }
    const col = e.dataTransfer.getData('text/column');
    if (!col) return;
    const id = `${col}_${Date.now()}`;
    setFields(prev => [...prev, { id, column: col, x: pt.x, y: pt.y, fs: 9 }]);
    setSelectedId(id);
  };

  // Déplacement d'un champ déjà posé
  const startMove = (e, f) => {
    e.stopPropagation();
    setSelectedId(f.id);
    setDrag({ id: f.id, mouseX: e.clientX, mouseY: e.clientY, fieldX: f.x, fieldY: f.y });
  };
  const onMove = useCallback((e) => {
    if (!drag) return;
    const dx = (e.clientX - drag.mouseX) / (SCALE * zoom);
    const dy = (e.clientY - drag.mouseY) / (SCALE * zoom);
    setFields(prev => prev.map(f => f.id === drag.id
      ? { ...f, x: Math.round((drag.fieldX + dx) * 10) / 10, y: Math.round((drag.fieldY + dy) * 10) / 10 }
      : f));
  }, [drag, zoom]);

  // Redimensionnement d'un champ logo (poignée bas-droite)
  const [resize, setResize] = useState(null);
  const startResize = (e, f) => {
    e.stopPropagation(); e.preventDefault();
    setSelectedId(f.id);
    setResize({ id: f.id, mouseX: e.clientX, mouseY: e.clientY, w: f.w || 120, h: f.h || 60 });
  };
  const onResize = useCallback((e) => {
    if (!resize) return;
    const dw = (e.clientX - resize.mouseX) / (SCALE * zoom);
    const dh = (e.clientY - resize.mouseY) / (SCALE * zoom);
    setFields(prev => prev.map(f => f.id === resize.id
      ? { ...f, w: Math.max(20, Math.round((resize.w + dw) * 10) / 10), h: Math.max(15, Math.round((resize.h + dh) * 10) / 10) }
      : f));
  }, [resize, zoom]);

  useEffect(() => {
    const up = () => { setDrag(null); setResize(null); };
    const move = (e) => { onMove(e); onResize(e); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [onMove, onResize]);

  const updateField = (id, key, val) => {
    const strKeys = ['column', 'align'];
    const boolKeys = ['fit'];
    setFields(prev => prev.map(f => f.id === id
      ? { ...f, [key]: strKeys.includes(key) ? val : (boolKeys.includes(key) ? !!val : (parseFloat(val) || 0)) }
      : f));
  };
  const removeField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/rapports/${rapportId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom, base_source: baseSource || null, table_source: tableSource || null,
          dans_menu: dansMenu,
          dans_menu_impression: dansMenuImpression,
          config: { offset_y: offsetY, fields: fields.map(f => {
            if (f.type === 'logo') return { id: f.id, type: 'logo', x: f.x, y: f.y, w: f.w, h: f.h };
            if (f.type === 'date') return { id: f.id, type: 'date', x: f.x, y: f.y, fs: f.fs, w: f.w || 0, align: f.align || 'left', fit: f.fit !== false };
            if (f.type === 'region') return { id: f.id, type: 'region', x: f.x, y: f.y, fs: f.fs, w: f.w || 0, align: f.align || 'left', fit: f.fit !== false };
            return { id: f.id, column: f.column, x: f.x, y: f.y, fs: f.fs, w: f.w || 0, align: f.align || 'left', fit: f.fit !== false };
          }) },
        }),
      });
      if (!res.ok) throw new Error('save');
      setMessage({ type: 'success', text: 'Rapport enregistré !' });
      onSaved?.();
      setTimeout(() => setMessage(null), 2500);
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur d\'enregistrement' });
    }
    setSaving(false);
  };

  const filteredCols = columns.filter(c =>
    colFilter === '' || c.name.toLowerCase().includes(colFilter.toLowerCase()));
  const selField = fields.find(f => f.id === selectedId);

  return (
    <div className="popup-overlay" data-testid="rapport-editor" style={{ zIndex: 10002 }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#f0f0f0', width: 'min(1500px, 98vw)', height: 'min(880px, 96vh)',
                    display: 'flex', flexDirection: 'column', border: '1px solid #555',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
        {/* En-tête */}
        <div style={{ background: '#1e3a8a', color: '#fff', padding: '8px 14px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700 }} data-testid="rapport-editor-title">
            Éditeur de rapport — {nom}
          </span>
          <button data-testid="rapport-editor-close" onClick={onClose}
                  style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Barre de config */}
        <div style={{ background: '#dbe6f0', padding: '8px 14px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Base :
            <select data-testid="rapport-base-select" value={baseSource} disabled={systeme}
                    onChange={e => { setBaseSource(e.target.value); setTableSource(''); }}
                    style={{ marginLeft: 6, padding: '3px 6px' }}>
              <option value="">— choisir —</option>
              {bases.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Table :
            <select data-testid="rapport-table-select" value={tableSource} disabled={systeme || !baseSource}
                    onChange={e => setTableSource(e.target.value)}
                    style={{ marginLeft: 6, padding: '3px 6px', minWidth: 160 }}>
              <option value="">— choisir —</option>
              {tables.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" data-testid="rapport-dans-menu" checked={dansMenu}
                   onChange={e => setDansMenu(e.target.checked)} />
            Menu « Formulaires » (vierge)
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" data-testid="rapport-dans-menu-impression" checked={dansMenuImpression}
                   onChange={e => setDansMenuImpression(e.target.checked)} />
            Menu « Impression » (avec données du client)
          </label>
          <label style={{ fontSize: 13 }}>Décalage Y :
            <input type="number" value={offsetY} onChange={e => setOffsetY(parseFloat(e.target.value) || 0)}
                   style={{ width: 70, marginLeft: 6, padding: '3px 6px' }} />
          </label>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="win-btn" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} style={{ padding: '3px 8px' }}>−</button>
            <span style={{ fontSize: 12 }}>{Math.round(zoom * 100)}%</span>
            <button className="win-btn" onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={{ padding: '3px 8px' }}>+</button>
            <button className="win-btn" onClick={() => fitToPane(natW, imgRef.current?.naturalHeight)}
                    title="Ajuster à la fenêtre" style={{ padding: '3px 8px' }}>⤢ Ajuster</button>
            <button className="win-btn" data-testid="rapport-save-btn" disabled={saving} onClick={save}
                    style={{ padding: '5px 16px', background: '#16a34a', color: '#fff', fontWeight: 700 }}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </span>
          {message && (
            <span data-testid="rapport-editor-msg" style={{ fontSize: 13, fontWeight: 700,
                  color: message.type === 'success' ? '#16a34a' : '#dc2626' }}>{message.text}</span>
          )}
        </div>

        {/* Corps : palette | pdf | propriétés */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Palette colonnes */}
          <div style={{ width: 230, borderRight: '1px solid #aaa', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 13, background: '#e5e7eb' }}>
              Champs de la table
            </div>
            <div style={{ padding: '6px', borderBottom: '1px solid #ddd', background: '#eef2ff' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3730a3', marginBottom: 4 }}>Éléments spéciaux</div>
              <div draggable data-testid="rapport-special-logo"
                   onDragStart={e => e.dataTransfer.setData('text/special', 'logo')}
                   title="Logo selon la région de la demande"
                   style={{ padding: '5px 8px', fontSize: 12.5, cursor: 'grab', border: '1px dashed #6366f1',
                            borderRadius: 4, marginBottom: 4, background: '#fff' }}>
                🖼 Logo (région)
              </div>
              <div draggable data-testid="rapport-special-date"
                   onDragStart={e => e.dataTransfer.setData('text/special', 'date')}
                   title="Date du jour à l'impression"
                   style={{ padding: '5px 8px', fontSize: 12.5, cursor: 'grab', border: '1px dashed #6366f1',
                            borderRadius: 4, background: '#fff', marginBottom: 4 }}>
                📅 Date du jour
              </div>
              <div draggable data-testid="rapport-special-region"
                   onDragStart={e => e.dataTransfer.setData('text/special', 'region')}
                   title="Nom de la région selon la demande (ex. 90 → Côte-Nord)"
                   style={{ padding: '5px 8px', fontSize: 12.5, cursor: 'grab', border: '1px dashed #6366f1',
                            borderRadius: 4, background: '#fff' }}>
                🗺 Nom de la région
              </div>
            </div>
            <input placeholder="Filtrer…" value={colFilter} onChange={e => setColFilter(e.target.value)}
                   data-testid="rapport-col-filter"
                   style={{ margin: 6, padding: '4px 6px', fontSize: 12 }} />
            <div style={{ flex: 1, overflow: 'auto' }}>
              {!tableSource && <div style={{ padding: 10, fontSize: 12, color: '#888' }}>Choisissez une base et une table.</div>}
              {filteredCols.map(c => (
                <div key={c.name} draggable data-testid={`rapport-col-${c.name}`}
                     onDragStart={e => e.dataTransfer.setData('text/column', c.name)}
                     title={c.type}
                     style={{ padding: '5px 10px', fontSize: 12.5, cursor: 'grab', borderBottom: '1px solid #eee' }}
                     onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {c.name}
                </div>
              ))}
            </div>
          </div>

          {/* Zone PDF */}
          <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', background: '#6b7280', padding: 16 }}>
            <div style={{ position: 'relative', display: 'inline-block' }}
                 onDrop={handleDrop} onDragOver={e => e.preventDefault()}
                 data-testid="rapport-pdf-canvas">
              <img ref={imgRef} src={imgUrl} alt="template"
                   onLoad={e => { setNatW(e.target.naturalWidth); fitToPane(e.target.naturalWidth, e.target.naturalHeight); }}
                   style={{ display: 'block',
                            maxWidth: 'none',
                            width: natW ? natW * zoom : 'auto' }}
                   draggable={false} />
              {fields.map(f => {
                const isLogo = f.type === 'logo';
                const isDate = f.type === 'date';
                const isRegion = f.type === 'region';
                const label = isLogo ? 'LOGO' : (isDate ? 'Date du jour' : (isRegion ? 'Nom région' : f.column));
                if (isLogo) {
                  return (
                    <div key={f.id} data-testid={`rapport-field-${f.id}`}
                         onMouseDown={e => startMove(e, f)}
                         style={{ position: 'absolute',
                                  left: f.x * SCALE * zoom,
                                  top: (f.y + offsetY) * SCALE * zoom,
                                  width: (f.w || 120) * SCALE * zoom,
                                  height: (f.h || 60) * SCALE * zoom,
                                  border: `2px solid ${selectedId === f.id ? '#ef4444' : '#16a34a'}`,
                                  background: selectedId === f.id ? 'rgba(239,68,68,0.18)' : 'rgba(22,163,74,0.15)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: '#14532d', fontSize: 11, fontWeight: 700,
                                  cursor: 'move', userSelect: 'none' }}>
                      🖼 LOGO
                      <div onMouseDown={e => startResize(e, f)}
                           style={{ position: 'absolute', right: -5, bottom: -5, width: 12, height: 12,
                                    background: '#16a34a', border: '1px solid #fff', cursor: 'nwse-resize' }} />
                    </div>
                  );
                }
                const fontPx = Math.max(6, f.fs * SCALE * zoom);
                const ascentPx = fontPx * 0.8;               // baseline ≈ top + ascent
                const baseTop = (f.y + offsetY) * SCALE * zoom; // y = baseline PDF (insert_text)
                const accent = selectedId === f.id ? '#ef4444'
                             : (isDate ? '#a855f7' : (isRegion ? '#f97316' : '#3b82f6'));
                const zonePx = (f.w || 0) * SCALE * zoom;     // largeur zone réservée (0 = aucune)
                const justify = f.align === 'center' ? 'center' : (f.align === 'right' ? 'flex-end' : 'flex-start');
                return (
                  <div key={f.id} data-testid={`rapport-field-${f.id}`}
                       onMouseDown={e => startMove(e, f)}
                       title="La ligne pointillée = ligne de base du texte à l'impression. Le cadre = zone réservée (Zone L.). Alignez la ligne sur le trait du document."
                       style={{ position: 'absolute',
                                left: f.x * SCALE * zoom,
                                top: baseTop - ascentPx,
                                width: zonePx > 0 ? zonePx : undefined,
                                display: 'flex', justifyContent: justify,
                                cursor: 'move', whiteSpace: 'nowrap', userSelect: 'none' }}>
                    <span style={{ display: 'inline-block',
                                   fontSize: fontPx, lineHeight: `${fontPx}px`,
                                   fontFamily: 'Helvetica, Arial, sans-serif', color: '#111',
                                   background: selectedId === f.id ? 'rgba(239,68,68,0.20)' : 'rgba(59,130,246,0.12)',
                                   outline: `1px solid ${accent}` }}>{label}</span>
                    {/* Ligne guide = baseline exacte du rendu PDF */}
                    <div style={{ position: 'absolute', left: 0, top: ascentPx,
                                  width: zonePx > 0 ? zonePx : 'calc(100% + 24px)',
                                  borderTop: `1px dashed ${accent}`, pointerEvents: 'none' }} />
                    {/* Cadre de la zone réservée (si Zone L. > 0) */}
                    {zonePx > 0 && (
                      <div style={{ position: 'absolute', left: 0, top: 0, width: zonePx, height: fontPx,
                                    border: `1px dashed ${accent}`, opacity: 0.55, pointerEvents: 'none' }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Propriétés du champ sélectionné + liste */}
          <div style={{ width: 250, borderLeft: '1px solid #aaa', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 13, background: '#e5e7eb' }}>
              Champs placés ({fields.length})
            </div>
            {selField && (
              <div style={{ padding: 10, borderBottom: '1px solid #ddd', fontSize: 12 }} data-testid="rapport-field-props">
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {selField.type === 'logo' ? '🖼 Logo (région)' : (selField.type === 'date' ? '📅 Date du jour' : (selField.type === 'region' ? '🗺 Nom de la région' : selField.column))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr', gap: 4, alignItems: 'center' }}>
                  <span>X</span><input type="number" value={selField.x} onChange={e => updateField(selField.id, 'x', e.target.value)} />
                  <span>Y</span><input type="number" value={selField.y} onChange={e => updateField(selField.id, 'y', e.target.value)} />
                  {selField.type === 'logo' ? (
                    <>
                      <span>Larg.</span><input type="number" value={selField.w} onChange={e => updateField(selField.id, 'w', e.target.value)} />
                      <span>Haut.</span><input type="number" value={selField.h} onChange={e => updateField(selField.id, 'h', e.target.value)} />
                    </>
                  ) : (
                    <>
                      <span title="Taille du texte (points), à ajuster selon le texte du PDF">Taille</span>
                      <input type="number" data-testid="rapport-field-fs" value={selField.fs}
                             onChange={e => updateField(selField.id, 'fs', e.target.value)} />
                      <span title="Largeur de la zone réservée (points). 0 = pas de zone : texte à gauche, taille fixe. Utile pour aligner/rétrécir un texte de longueur variable sur un PDF fixe.">Zone L.</span>
                      <input type="number" data-testid="rapport-field-w" value={selField.w || 0}
                             onChange={e => updateField(selField.id, 'w', e.target.value)} />
                      <span title="Alignement du texte dans la zone. « Droite » colle le texte à la fin de la zone (utile pour supprimer l'espace avant le texte figé qui suit).">Aligne</span>
                      <select data-testid="rapport-field-align" value={selField.align || 'left'}
                              onChange={e => updateField(selField.id, 'align', e.target.value)}>
                        <option value="left">Gauche</option>
                        <option value="center">Centre</option>
                        <option value="right">Droite</option>
                      </select>
                      <span title="Rétrécit automatiquement le texte s'il dépasse la largeur de la zone.">Ajuster</span>
                      <input type="checkbox" data-testid="rapport-field-fit"
                             checked={selField.fit !== false}
                             onChange={e => updateField(selField.id, 'fit', e.target.checked)}
                             style={{ justifySelf: 'start' }} />
                    </>
                  )}
                </div>
                <button className="win-btn" data-testid="rapport-field-delete" onClick={() => removeField(selField.id)}
                        style={{ marginTop: 8, padding: '4px 10px', background: '#dc2626', color: '#fff' }}>Supprimer ce champ</button>
              </div>
            )}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {fields.map(f => (
                <div key={f.id} onClick={() => setSelectedId(f.id)}
                     style={{ padding: '5px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #eee',
                              background: selectedId === f.id ? '#dbeafe' : 'transparent',
                              display: 'flex', justifyContent: 'space-between' }}>
                  <span>{f.type === 'logo' ? '🖼 Logo' : (f.type === 'date' ? '📅 Date' : (f.type === 'region' ? '🗺 Région' : f.column))}</span>
                  <span style={{ color: '#888' }}>{f.x},{f.y}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
